"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { RETRACTION_CHECKER_STRINGS } from "@/components/tools/retraction-checker";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import type { BatchLine } from "@/lib/tool-kit/identifiers/batch";
import { doiHref, type FoundDoi } from "@/lib/tool-kit/identifiers/doi-text";
import type { LookupFailure } from "@/lib/tool-kit/identifiers/lookup";
import {
  formatNoticeDate,
  isoNoticeDate,
  toCsv,
  type Notice as UpdateNotice,
  type WorkAssessment,
  type WorkStatus,
} from "@/lib/tool-kit/identifiers/retraction";

/*
 * The Retraction Checker's results: the summary counts, the table of DOIs with their
 * notices, and the CSV of the same. Every word comes from the widget's strings.
 */

type Strings = typeof RETRACTION_CHECKER_STRINGS;

/** One DOI from the reader's list, and what Crossref said about it. */
export interface RetractionRow extends BatchLine<FoundDoi> {
  result?: WorkAssessment;
  reason?: LookupFailure;
}

type Row = RetractionRow;

const STATUS_KEY: Record<WorkStatus, keyof Strings> = {
  retracted: "statusRetracted",
  withdrawn: "statusWithdrawn",
  "partially-retracted": "statusPartial",
  concern: "statusConcern",
  corrected: "statusCorrected",
  updated: "statusUpdated",
  none: "statusNone",
  notice: "statusNotice",
};

const TYPE_KEY = new Map<string, keyof Strings>([
  ["retraction", "typeRetraction"],
  ["partial_retraction", "typePartialRetraction"],
  ["withdrawal", "typeWithdrawal"],
  ["removal", "typeRemoval"],
  ["expression_of_concern", "typeConcern"],
  ["correction", "typeCorrection"],
  ["erratum", "typeErratum"],
  ["corrigendum", "typeCorrigendum"],
]);

const SERIOUS = new Set<WorkStatus>([
  "retracted",
  "withdrawn",
  "partially-retracted",
]);

const BADGE =
  "inline-block whitespace-nowrap rounded-md border px-2 py-0.5 font-medium text-xs";
const TONE_SERIOUS = `${BADGE} border-destructive/40 bg-destructive/10 text-destructive`;
const TONE_CONCERN = `${BADGE} border-amber-500/40 bg-amber-100 text-amber-900 dark:bg-amber-400/15 dark:text-amber-200`;
const TONE_UPDATE = `${BADGE} border-sky-500/40 bg-sky-100 text-sky-900 dark:bg-sky-400/15 dark:text-sky-200`;
const TONE_NEUTRAL = `${BADGE} border-border bg-muted text-muted-foreground`;
const LINK =
  "break-all font-mono text-primary text-xs underline underline-offset-2";

function statusText(row: Row, strings: Strings): string {
  if (row.result) {
    return strings[STATUS_KEY[row.result.status]];
  }
  return row.reason === "not-found"
    ? strings.statusNotInCrossref
    : strings.statusUnchecked;
}

function toneOf(row: Row): string {
  const status = row.result?.status;
  if (status && SERIOUS.has(status)) {
    return TONE_SERIOUS;
  }
  if (status === "concern") {
    return TONE_CONCERN;
  }
  return status === "corrected" || status === "updated"
    ? TONE_UPDATE
    : TONE_NEUTRAL;
}

function failureText(row: Row, strings: Strings, max: number): string {
  if (row.state === "over") {
    return t(strings.overLimit, { max });
  }
  const messages: Partial<Record<LookupFailure, string>> = {
    "not-found": strings.failNotFound,
    "rate-limited": strings.failRateLimited,
    network: strings.failNetwork,
  };
  return (row.reason && messages[row.reason]) || strings.failUnavailable;
}

function typeText(notice: UpdateNotice, strings: Strings): string {
  const key = TYPE_KEY.get(notice.type);
  return key ? strings[key] : notice.label;
}

function sourceText(notice: UpdateNotice, strings: Strings): string {
  return notice.sources
    .map((source) => {
      if (source === "publisher") {
        return strings.sourcePublisher;
      }
      if (source !== "retraction-watch") {
        return source;
      }
      return notice.recordIds.length > 0
        ? t(strings.sourceRecord, { id: notice.recordIds.join(", ") })
        : strings.sourceRetractionWatch;
    })
    .join(" · ");
}

function NoticeList({
  row,
  strings,
  locale,
  max,
}: {
  row: Row;
  strings: Strings;
  locale: string;
  max: number;
}) {
  const result = row.result;
  if (!result) {
    return (
      <p className="text-muted-foreground">{failureText(row, strings, max)}</p>
    );
  }
  return (
    <ul className="space-y-2">
      {result.notices.map((notice) => (
        <li key={`${notice.doi}|${notice.type}`}>
          <NoticeLine
            isNotice={result.status === "notice"}
            locale={locale}
            notice={notice}
            strings={strings}
          />
          {notice.doi ? (
            <>
              <br />
              <a
                className={LINK}
                href={doiHref(notice.doi)}
                rel="noopener noreferrer"
                target="_blank"
              >
                {notice.sameDoi ? strings.sameDoi : notice.doi}
              </a>
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** "Retraction  Sep 18, 2012 · Retraction Watch, record 2855", or "Retraction notice for". */
function NoticeLine({
  notice,
  isNotice,
  strings,
  locale,
}: {
  notice: UpdateNotice;
  isNotice: boolean;
  strings: Strings;
  locale: string;
}) {
  const type = typeText(notice, strings);
  if (isNotice) {
    return (
      <span className="font-medium">{t(strings.noticeFor, { type })}</span>
    );
  }
  const date = formatNoticeDate(notice.date, locale) || strings.undated;
  return (
    <>
      <span className="font-medium">{type}</span>{" "}
      <span className="text-muted-foreground">
        {date} · {sourceText(notice, strings)}
      </span>
    </>
  );
}

function DoiCell({ row, strings }: { row: Row; strings: Strings }) {
  const fmt = useToolNumber();
  const { doi, line, problems } = row.parsed;
  return (
    <td className={`${TD} min-w-44 align-top`}>
      <a
        className={LINK}
        href={doiHref(doi)}
        rel="noopener noreferrer"
        target="_blank"
      >
        {doi}
      </a>
      <p className={HINT}>{t(strings.lineRef, { line: fmt(line) })}</p>
      {problems.includes("unusual-character") ? (
        <p className={HINT}>{strings.problemCharacter}</p>
      ) : null}
      {problems.includes("split-line") ? (
        <p className={HINT}>{strings.problemSplit}</p>
      ) : null}
    </td>
  );
}

function ResultsTable({
  rows,
  strings,
  locale,
  max,
}: {
  rows: Row[];
  strings: Strings;
  locale: string;
  max: number;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {strings.colDoi}
            </th>
            <th className={TH} scope="col">
              {strings.colTitle}
            </th>
            <th className={TH} scope="col">
              {strings.colStatus}
            </th>
            <th className={TH} scope="col">
              {strings.colNotices}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <DoiCell row={row} strings={strings} />
              <td className={`${TD} min-w-48 align-top`}>
                {row.result ? row.result.title || strings.untitled : ""}
                {row.result?.container ? (
                  <p className={HINT}>
                    {[row.result.container, row.result.year]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                ) : null}
              </td>
              <td className={`${TD} align-top`}>
                <span className={toneOf(row)}>{statusText(row, strings)}</span>
              </td>
              <td className={`${TD} min-w-56 align-top`}>
                <NoticeList
                  locale={locale}
                  max={max}
                  row={row}
                  strings={strings}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One row per notice, so a spreadsheet can sort and filter them. */
export function retractionCsv(rows: Row[], strings: Strings): string {
  const table: string[][] = [
    [
      strings.csvDoi,
      strings.csvTitle,
      strings.csvStatus,
      strings.csvNotice,
      strings.csvDate,
      strings.csvSource,
      strings.csvNoticeDoi,
    ],
  ];
  for (const row of rows) {
    const base = [
      row.parsed.doi,
      row.result?.title ?? "",
      statusText(row, strings),
    ];
    const notices = row.result?.notices ?? [];
    if (notices.length === 0) {
      table.push([...base, "", "", "", ""]);
    }
    for (const notice of notices) {
      table.push([
        ...base,
        typeText(notice, strings),
        isoNoticeDate(notice.date),
        sourceText(notice, strings),
        notice.doi,
      ]);
    }
  }
  return toCsv(table);
}

function Summary({ rows, strings }: { rows: Row[]; strings: Strings }) {
  const fmt = useToolNumber();
  const count = (test: (status: WorkStatus) => boolean) =>
    rows.filter((row) => row.result && test(row.result.status)).length;
  return (
    <StatGrid>
      <StatCard
        emphasis
        label={strings.statSerious}
        value={fmt(count((status) => SERIOUS.has(status)))}
      />
      <StatCard
        label={strings.statConcern}
        value={fmt(count((status) => status === "concern"))}
      />
      <StatCard
        label={strings.statCorrected}
        value={fmt(
          count((status) => status === "corrected" || status === "updated")
        )}
      />
      <StatCard
        label={strings.statNone}
        value={fmt(count((status) => status === "none"))}
      />
    </StatGrid>
  );
}

/** The summary, the filter, the table and the caveat under it. */
export function RetractionResults({
  rows,
  strings,
  max,
}: {
  rows: Row[];
  strings: Strings;
  max: number;
}) {
  const locale = useToolLocale();
  const [onlyNotices, setOnlyNotices] = useState(false);
  const shown = onlyNotices
    ? rows.filter((row) => row.result && row.result.status !== "none")
    : rows;
  return (
    <>
      <Summary rows={rows} strings={strings} />
      <Checkbox
        checked={onlyNotices}
        label={strings.onlyNotices}
        onChange={setOnlyNotices}
      />
      {shown.length > 0 ? (
        <ResultsTable
          locale={locale}
          max={max}
          rows={shown}
          strings={strings}
        />
      ) : (
        <Notice>{strings.noneToShow}</Notice>
      )}
      <p className="text-muted-foreground text-sm">{strings.caveat}</p>
    </>
  );
}
